import React, { useEffect, useState } from 'react';

import { Box, IconButton, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

const dateFilterContainerSx = {
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	gap: '8px',
	width: { xs: '100%', sm: 'auto' },
};

const dateControlSx = {
	width: { xs: '100%', sm: 180 },
	minWidth: { xs: '100%', sm: 180 },
	flex: { xs: '1 1 100%', sm: '0 0 auto' },
};

const clearButtonSx = {
	border: '1px solid #b7c0c7',
	borderRadius: '25px',
	color: '#c62828',
	'&:hover': {
		backgroundColor: '#d3d3d3',
	},
	'&.Mui-disabled': {
		color: '#9e9e9e',
		borderColor: '#d0d0d0',
	},
};

// Helper function to format Date objects into API query string format (YYYYMMDDHHMMSS)
// Converts local date/time to UTC to avoid timezone discrepancies with the backend
function formatQueryDate(dateValue, endOfDay = false) {
	if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
		return '';
	}

	// Create a new date representing the start or end of the selected day in local time
	const localDate = new Date(dateValue);
	if (endOfDay) {
		localDate.setHours(23, 59, 59, 999);
	} else {
		localDate.setHours(0, 0, 0, 0);
	}

	// Convert to UTC using getUTC* methods
	const year = String(localDate.getUTCFullYear());
	const month = String(localDate.getUTCMonth() + 1).padStart(2, '0');
	const day = String(localDate.getUTCDate()).padStart(2, '0');
	const hours = String(localDate.getUTCHours()).padStart(2, '0');
	const minutes = String(localDate.getUTCMinutes()).padStart(2, '0');
	const seconds = String(localDate.getUTCSeconds()).padStart(2, '0');

	return `${year}${month}${day}${hours}${minutes}${seconds}`;
}


export default function DateRangeFilterControl({ onFilterChange }) {
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);

	// Trigger filter change whenever start or end date changes, passing formatted date strings or null if not set
	useEffect(() => {
		if (onFilterChange) {
			onFilterChange(
				formatQueryDate(startDate, false),
				formatQueryDate(endDate, true)
			);
		}
	}, [onFilterChange, startDate, endDate]);

	// Ensure start date is not after end date when it changes
	const handleStartDateChange = (nextStartDate) => {
		setStartDate(nextStartDate);

		if (nextStartDate && endDate && nextStartDate > endDate) {
			setEndDate(nextStartDate);
		}
	};

	// Ensure end date is not before start date when it changes
	const handleEndDateChange = (nextEndDate) => {
		setEndDate(nextEndDate);

		if (nextEndDate && startDate && nextEndDate < startDate) {
			setStartDate(nextEndDate);
		}
	};

	// Clear both dates and trigger filter change with null values
	const clearDateRange = () => {
		setStartDate(null);
		setEndDate(null);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Box sx={dateFilterContainerSx}>
				<DatePicker
					label="Start Date"
					value={startDate}
					onChange={handleStartDateChange}
					maxDate={endDate || undefined}
					slotProps={{
						textField: {
							size: 'small',
							sx: dateControlSx,
							// InputLabelProps: { shrink: true },
						},
					}}
				/>
				<DatePicker
					label="End Date"
					value={endDate}
					onChange={handleEndDateChange}
					minDate={startDate || undefined}
					slotProps={{
						textField: {
							size: 'small',
							sx: dateControlSx,
							// InputLabelProps: { shrink: true },
						},
					}}
				/>
				<Tooltip title="Clear date range">
					<span>
						<IconButton
							size="small"
							onClick={clearDateRange}
							disabled={!startDate && !endDate}
							aria-label="Clear date range"
							sx={clearButtonSx}
						>
							<FilterAltOffIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Box>
		</LocalizationProvider>
	);
}
